// Google Maps API Loader - Ensures the API is only loaded once
let isLoading = false;
let isLoaded = false;
let loadPromise = null;

export const loadGoogleMapsAPI = async () => {
  // If already loaded, return immediately
  if (isLoaded && window.google && window.google.maps) {
    return Promise.resolve();
  }

  // If currently loading, return the existing promise
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start loading
  isLoading = true;
  
  loadPromise = new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script exists, wait for it to load
      if (window.google && window.google.maps) {
        isLoaded = true;
        isLoading = false;
        resolve();
        return;
      }
      
      // Wait for existing script to load
      existingScript.addEventListener('load', () => {
        isLoaded = true;
        isLoading = false;
        resolve();
      });
      
      existingScript.addEventListener('error', () => {
        isLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      });
      
      return;
    }

    // Create new script
    const script = document.createElement('script');
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!googleMapsApiKey) {
      isLoading = false;
      reject(new Error('Google Maps API key not found'));
      return;
    }

    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Add a small delay to ensure the API is fully initialized
      setTimeout(() => {
        if (window.google && window.google.maps) {
          isLoaded = true;
          isLoading = false;
          console.log('Google Maps API loaded successfully');
          resolve();
        } else {
          isLoading = false;
          reject(new Error('Google Maps API script loaded but objects not available'));
        }
      }, 100);
    };

    script.onerror = (error) => {
      isLoading = false;
      console.error('Failed to load Google Maps API script:', error);
      reject(new Error(`Failed to load Google Maps API: ${error.message || 'Unknown error'}`));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

// Check if Google Maps API is already loaded
export const isGoogleMapsLoaded = () => {
  return !!(window.google && window.google.maps && window.google.maps.places);
};

// Initialize Google Places service
export const initGooglePlacesService = async () => {
  await loadGoogleMapsAPI();
  
  // Wait a bit for the API to fully initialize after loading
  let retries = 0;
  const maxRetries = 10;
  
  while ((!window.google || !window.google.maps || !window.google.maps.places) && retries < maxRetries) {
    await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms
    retries++;
  }
  
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      throw new Error('Google Maps API key not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables.');
    }
    throw new Error('Google Maps API not loaded properly after multiple attempts. Check your API key and network connection.');
  }
  
  // Use the new AutocompleteSuggestion API instead of deprecated AutocompleteService
  if (window.google.maps.places.AutocompleteSuggestion) {
    return new window.google.maps.places.AutocompleteSuggestion();
  } else {
    throw new Error('AutocompleteSuggestion API not available. Please ensure you are using a recent version of the Google Maps JavaScript API.');
  }
};
